import { getValidToken } from '../utils/tokenManager';
import { environment } from '../config/environment';

export interface QuizGenerationRequest {
  prompt: string;
  difficulty?: 'easy' | 'normal' | 'hard';
  toneOfVoice?: 'professional' | 'casual' | 'fun';
  numberOfQuestions?: number;
  language?: 'English' | 'Spanish' | 'French' | 'German' | 'Norwegian';
  qaCheck?: boolean;
}

export interface Question {
  id: string;
  question: string;
  alternatives: string[];
  correctAnswer: number;
}

export interface Finding {
  question_number: number;
  message: string;
}

export interface FactCheckResult {
  findings: Finding[];
  fact_check_passed: boolean;
  confidence: number;
}

export interface Quiz {
  title: string;
  questions: Question[];
  fact_check?: FactCheckResult;
}

const LAMBDA_ENDPOINT = environment.api.quizGenerator;

const getAuthToken = async (): Promise<string | null> => {
  return await getValidToken();
};

export const generateQuiz = async (request: QuizGenerationRequest): Promise<Quiz> => {
  console.log('Starting quiz generation with request:', request);
  const startTime = Date.now();

  // Get authentication token
  const token = await getAuthToken();
  if (!token) {
    throw new Error('No authentication token found. Please sign in to create quizzes.');
  }

  // Map difficulty from UI to Lambda format
  const difficultyMap: { [key: string]: string } = {
    'easy': 'easy',
    'normal': 'medium',
    'hard': 'hard'
  };

  const requestPayload = {
    prompt_text: request.prompt,
    n_questions: request.numberOfQuestions || 10,
    difficulty: difficultyMap[request.difficulty || 'normal'],
    tone_of_voice: request.toneOfVoice || 'casual',
    language: request.language || 'English',
    qa_check: request.qaCheck || false,
  };

  console.log('Sending request to Lambda:', requestPayload);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    console.log('2-minute timeout reached, aborting request');
    controller.abort();
  }, 120000); // 2 minute timeout

  try {
    console.log('Making fetch request to:', LAMBDA_ENDPOINT);
    const response = await fetch(LAMBDA_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestPayload),
      signal: controller.signal,
    });

    const fetchDuration = Date.now() - startTime;
    console.log(`Fetch completed in ${fetchDuration}ms, status: ${response.status}`);

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.error('Response not OK:', response.status, response.statusText);
      const responseText = await response.text();
      console.error('Response body:', responseText);

      if (response.status === 500) {
        throw new Error('Oops! 🤖 Our AI got a bit confused and made a mistake. This sometimes happens when content doesn\'t quite fit our guidelines. Please try again with a different topic or phrasing!');
      }

      throw new Error(`Failed to generate quiz: ${response.statusText} (${response.status})`);
    }

    console.log('Parsing response JSON...');
    const data = await response.json();
    console.log('Response parsed successfully:', data);

    const totalDuration = Date.now() - startTime;
    console.log(`Quiz generation completed in ${totalDuration}ms`);

    // Transform the response to match our Quiz interface
    return {
      title: `Quiz about: ${request.prompt}`,
      questions: data.questions.map((q: any) => ({
        id: q.id,
        question: q.question,
        alternatives: q.answers,
        correctAnswer: q.correct_index,
      })),
      fact_check: data.fact_check,
    };
  } catch (error) {
    const totalDuration = Date.now() - startTime;
    console.error(`Error after ${totalDuration}ms:`, error);

    clearTimeout(timeoutId);

    if (error instanceof Error) {
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });

      if (error.name === 'AbortError') {
        console.error('Request was aborted (timeout)');
        throw new Error('Request timed out after 2 minutes. Please try again.');
      }

      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('Network error occurred');
        throw new Error('Network error: Please check your internet connection and try again.');
      }
    }

    throw error;
  }
};

export interface RegenerateQuestionRequest {
  quizContext: Array<{
    id: string;
    question: string;
    answers: string[];
    correct_index: number;
  }>;
  questionIndex: number;
  difficulty?: 'easy' | 'medium' | 'hard';
  toneOfVoice?: 'professional' | 'casual' | 'fun';
  language?: string;
  originalPrompt?: string;
}

export const regenerateQuestion = async (request: RegenerateQuestionRequest): Promise<Question> => {
  console.log('Starting question regeneration with request:', request);
  const startTime = Date.now();

  // Get authentication token
  const token = await getAuthToken();
  if (!token) {
    throw new Error('No authentication token found. Please sign in.');
  }

  const requestPayload = {
    quiz_context: request.quizContext,
    question_index: request.questionIndex,
    difficulty: request.difficulty || 'medium',
    tone_of_voice: request.toneOfVoice || 'casual',
    language: request.language || 'English',
    original_prompt: request.originalPrompt || '',
  };

  console.log('Sending regenerate request to Lambda:', requestPayload);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    console.log('30-second timeout reached, aborting request');
    controller.abort();
  }, 30000); // 30 second timeout

  try {
    const regenerateEndpoint = `${LAMBDA_ENDPOINT}/regenerate`;
    console.log('Making fetch request to:', regenerateEndpoint);
    const response = await fetch(regenerateEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestPayload),
      signal: controller.signal,
    });

    const fetchDuration = Date.now() - startTime;
    console.log(`Fetch completed in ${fetchDuration}ms, status: ${response.status}`);

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.error('Response not OK:', response.status, response.statusText);
      const responseText = await response.text();
      console.error('Response body:', responseText);

      if (response.status === 500) {
        throw new Error('Failed to regenerate question. Please try again.');
      }

      throw new Error(`Failed to regenerate question: ${response.statusText} (${response.status})`);
    }

    console.log('Parsing response JSON...');
    const data = await response.json();
    console.log('Response parsed successfully:', data);

    const totalDuration = Date.now() - startTime;
    console.log(`Question regeneration completed in ${totalDuration}ms`);

    // Transform the response to match our Question interface
    return {
      id: data.question.id,
      question: data.question.question,
      alternatives: data.question.answers,
      correctAnswer: data.question.correct_index,
    };
  } catch (error) {
    const totalDuration = Date.now() - startTime;
    console.error(`Error after ${totalDuration}ms:`, error);

    clearTimeout(timeoutId);

    if (error instanceof Error) {
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });

      if (error.name === 'AbortError') {
        console.error('Request was aborted (timeout)');
        throw new Error('Request timed out. Please try again.');
      }

      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('Network error occurred');
        throw new Error('Network error: Please check your internet connection and try again.');
      }
    }

    throw error;
  }
};

export interface FactCheckQuizRequest {
  questions: Array<{
    id: string;
    question: string;
    answers: string[];
    correct_index: number;
  }>;
  originalPrompt: string;
}

export const factCheckQuiz = async (request: FactCheckQuizRequest): Promise<FactCheckResult> => {
  console.log('Starting fact-check with request:', request);
  const startTime = Date.now();

  // Get authentication token
  const token = await getAuthToken();
  if (!token) {
    throw new Error('No authentication token found. Please sign in.');
  }

  const requestPayload = {
    questions: request.questions,
    original_prompt: request.originalPrompt,
  };

  console.log('Sending fact-check request to Lambda:', requestPayload);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    console.log('60-second timeout reached, aborting request');
    controller.abort();
  }, 60000); // 60 second timeout for fact-checking

  try {
    const factCheckEndpoint = `${LAMBDA_ENDPOINT}/fact-check`;
    console.log('Making fetch request to:', factCheckEndpoint);
    const response = await fetch(factCheckEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestPayload),
      signal: controller.signal,
    });

    const fetchDuration = Date.now() - startTime;
    console.log(`Fetch completed in ${fetchDuration}ms, status: ${response.status}`);

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.error('Response not OK:', response.status, response.statusText);
      const responseText = await response.text();
      console.error('Response body:', responseText);

      if (response.status === 500) {
        throw new Error('Failed to fact-check quiz. Please try again.');
      }

      throw new Error(`Failed to fact-check quiz: ${response.statusText} (${response.status})`);
    }

    console.log('Parsing response JSON...');
    const data = await response.json();
    console.log('Response parsed successfully:', data);

    const totalDuration = Date.now() - startTime;
    console.log(`Fact-check completed in ${totalDuration}ms`);

    // Return the fact_check result
    return data.fact_check;
  } catch (error) {
    const totalDuration = Date.now() - startTime;
    console.error(`Error after ${totalDuration}ms:`, error);

    clearTimeout(timeoutId);

    if (error instanceof Error) {
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });

      if (error.name === 'AbortError') {
        console.error('Request was aborted (timeout)');
        throw new Error('Fact-check timed out. Please try again.');
      }

      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('Network error occurred');
        throw new Error('Network error: Please check your internet connection and try again.');
      }
    }

    throw error;
  }
};